import {
  DependentsChoice,
  PersonInfo,
  ProductLineInterestChange,
  SubscriberPresenter,
  SubChecklistCard,
  Subscriber,
  SubscriberTask,
  UpdatedPlanInfo,
} from 'types/electionService';
import { sortDependentsInSubscriber } from 'global/services-helpers/electionService';
import { createSubscriberPresenter } from 'global/utils/election/electionPresenters';
import { makeAutoObservable, runInAction, toJS } from 'mobx';
import { ChecklistOption } from 'types/enrollmentService';
import {
  addOrUpdateDependent,
  getSubscriberById,
  getSubscriberWithChoices,
  removeSubscriberDependent,
  updateSubscriberInfo,
} from 'api/operations/electionService';
import {
  getChoiceByProductLineId,
  getInterestedProductLineByProductLineId,
} from 'global/utils/election/productLineElection';
import SubscriberDependentDelegate from './dependents/subDependentDelegate';
import ProductLineRateModalDelegate from './productLineRateModalDelegate';

export interface SubscriberDelegateConstructorParams {
  subscriber: Subscriber;
  periodId: string;
  updateElectionChanges?: () => Promise<void>;
}

/** A basic Subscriber abstraction that allows to perform actions on a subscriber and its dependents. */
export default class SubscriberDelegate {
  private _subscriber: Subscriber;
  private _periodId: string;
  private _dependentDelegates: SubscriberDependentDelegate[] = [];
  public productLineRateModalDelegate: ProductLineRateModalDelegate;

  public get subscriber(): Subscriber {
    return this._subscriber;
  }

  public get subscriberPresenter(): SubscriberPresenter {
    return createSubscriberPresenter(toJS(this._subscriber));
  }

  public get subscriberDependentsDelegates(): SubscriberDependentDelegate[] {
    return this._dependentDelegates;
  }

  public get subscriberTasks(): { active: SubscriberTask[]; inactive: SubscriberTask[] } {
    return {
      active: this._subscriber.tasks.filter((task) => task.active),
      inactive: this._subscriber.tasks.filter((task) => !task.active),
    };
  }

  private _getDependentDelegate(dependentId: string): SubscriberDependentDelegate | undefined {
    return this._dependentDelegates.find((delegate) => delegate.dependent?.id === dependentId);
  }

  public getTaskChecklistOptions(entityId: string): ChecklistOption<SubChecklistCard>[] {
    return this._subscriber?.tasks?.find((task) => task.entityId === entityId)?.checklist?.checklistOptions || [];
  }

  public setSubscriber(subscriber: Subscriber): void {
    this._subscriber = sortDependentsInSubscriber(subscriber);
    this._createDependentDelegates();
  }

  public async fetchSubscriber(): Promise<void> {
    const subscriber = await getSubscriberWithChoices(this._subscriber.id, this._periodId);
    runInAction(() => {
      this.setSubscriber(subscriber);
    });
  }

  private _createDependentDelegates(): void {
    this._dependentDelegates = this._subscriber.custom.dependents.map(
      (dependent) =>
        new SubscriberDependentDelegate({
          dependent,
          subscriberId: this._subscriber.id,
        }),
    );
  }

  public async updatePrimary(answers: Record<string, string>, periodId: string): Promise<void> {
    const convertedSubscriber = toJS(this._subscriber);

    const updatedSubscriber = {
      ...convertedSubscriber,
      periodId,
      common: {
        firstName: convertedSubscriber.common.firstName,
        lastName: convertedSubscriber.common.lastName,
        ...answers,
      },
    };

    await updateSubscriberInfo(periodId, updatedSubscriber);
    await runInAction(async () => {
      await this.fetchSubscriber();
    });
  }

  public async addDependent(dependent: PersonInfo): Promise<void> {
    const convertedSubscriber = toJS(this._subscriber);

    await addOrUpdateDependent(convertedSubscriber.id, { ...dependent, id: '' });
    await runInAction(async () => {
      await this.fetchSubscriber();
    });
  }

  public async updateDependent(dependent: PersonInfo): Promise<void> {
    const currentDependentDelegate = this._getDependentDelegate(dependent.id);
    if (currentDependentDelegate) {
      await currentDependentDelegate.updateDependent(dependent);
    }
  }

  public async removeDependent(dependentId: string): Promise<void> {
    const convertedSubscriber = toJS(this._subscriber);

    await removeSubscriberDependent(convertedSubscriber.id, dependentId);
    const subscriber = await getSubscriberById(convertedSubscriber.id);
    runInAction(() => {
      this.setSubscriber(subscriber);
    });
  }

  public async updateSelectedPlan(selectedPlanInfo: UpdatedPlanInfo): Promise<void> {
    const { productLineId, selectedPlanName, selectedPlanId } = selectedPlanInfo;

    const currentChoice = this._subscriber?.choice.find((choiceItem) => choiceItem.productLineId === productLineId);

    if (currentChoice && selectedPlanName) {
      currentChoice.productId = selectedPlanId;
      currentChoice.productName = selectedPlanName;
    }
    await updateSubscriberInfo(this._periodId, this._subscriber);
    await runInAction(async () => {
      await this.fetchSubscriber();
    });
  }

  public async updateDependentsInChoice({ productLineId, dependentIds }: DependentsChoice): Promise<void> {
    const currentChoice = getChoiceByProductLineId(this._subscriber.choice, productLineId) || null;

    if (!currentChoice) {
      return;
    }

    currentChoice.dependentIds = dependentIds;
    await updateSubscriberInfo(this._periodId, this._subscriber);
    await runInAction(async () => {
      await this.fetchSubscriber();
    });
  }

  public async updateDependentsInInterestedProductLines({
    productLineId,
    dependentIds,
  }: DependentsChoice): Promise<void> {
    const interestedProductLine = getInterestedProductLineByProductLineId(
      this._subscriber.interestedProductLines,
      productLineId,
    );
    if (!interestedProductLine) {
      return;
    }

    interestedProductLine.dependentIds = dependentIds;
    await updateSubscriberInfo(this._periodId, toJS(this._subscriber));
    await runInAction(async () => {
      await this.fetchSubscriber();
    });
  }

  public async updateProductLineInterest({ productLineId, selection }: ProductLineInterestChange): Promise<void> {
    const updatedLine = this._subscriber.interestedProductLines.find((item) => item.productLineId === productLineId);
    if (updatedLine) {
      updatedLine.value = selection;
    } else {
      this._subscriber.interestedProductLines.push({
        productLineId,
        dependentIds: [],
        value: selection,
      });
    }
    await updateSubscriberInfo(this._periodId, toJS(this._subscriber));
    await runInAction(async () => {
      await this.fetchSubscriber();
    });
  }

  public async updateClassAndResetProductSelection(className: string): Promise<void> {
    const updatedChoice = this._subscriber.choice.map((choice) => {
      return { ...choice, productId: '', productName: '' };
    });
    this._subscriber.employeeClass = className;
    this._subscriber.choice = updatedChoice;
    await updateSubscriberInfo(this._periodId, this._subscriber);
    await runInAction(async () => {
      await this.fetchSubscriber();
    });
  }

  constructor({ subscriber, periodId }: SubscriberDelegateConstructorParams) {
    this._periodId = periodId;
    this._subscriber = subscriber;
    this.productLineRateModalDelegate = new ProductLineRateModalDelegate({ periodId });
    this._createDependentDelegates();

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
